import axios from 'axios';

// Configuração base do axios
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Serviço de autenticação
export const authService = {
  // Login
  login: async (credentials) => {
    try {
      const response = await api.post('/login', credentials);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro no login' 
      };
    }
  },

  // Registro
  register: async (userData) => {
    try {
      const response = await api.post('/register', userData);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro no registro' 
      };
    }
  },

  // Logout
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  // Verificar se está autenticado
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },

  // Obter usuário atual
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
};

// Serviços para membros
export const memberService = {
  // Obter perfil do usuário logado
  getProfile: async () => {
    try {
      const response = await api.get('/profile');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar perfil' 
      };
    }
  },

  // Listar todos os membros
  getAll: async () => {
    try {
      const response = await api.get('/members');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar membros' 
      };
    }
  },

  // Buscar membros
  search: async (params = {}) => {
    try {
      const response = await api.get('/members/search', { params });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro na busca' 
      };
    }
  },

  // Buscar membro por ID
  getById: async (id) => {
    try {
      const response = await api.get(`/members/${id}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar membro' 
      };
    }
  },

  // Atualizar membro
  update: async (id, memberData) => {
    try {
      const response = await api.put(`/members/${id}`, memberData);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao atualizar membro' 
      };
    }
  },

  // Deletar membro
  delete: async (id) => {
    try {
      await api.delete(`/members/${id}`);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao deletar membro' 
      };
    }
  },

  // Ativar membro
  activate: async (id) => {
    try {
      await api.post(`/members/${id}/activate`);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao ativar membro' 
      };
    }
  },

  // Alterar senha
  changePassword: async (passwordData) => {
    try {
      await api.post('/change-password', passwordData);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao alterar senha' 
      };
    }
  },

  // Atualizar foto
  updatePhoto: async (photoUrl) => {
    try {
      const response = await api.post('/update-photo', { photoUrl });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao atualizar foto' 
      };
    }
  }
};

// Serviços para postagens
export const postService = {
  // Listar todas as postagens
  getAll: async () => {
    try {
      const response = await api.get('/posts');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar postagens' 
      };
    }
  },

  // Buscar postagem por ID
  getById: async (id) => {
    try {
      const response = await api.get(`/posts/${id}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar postagem' 
      };
    }
  },

  // Buscar postagens por autor
  getByAuthor: async (authorId) => {
    try {
      const response = await api.get(`/posts/author/${authorId}`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar postagens do autor' 
      };
    }
  },

  // Buscar postagens
  search: async (params = {}) => {
    try {
      const response = await api.get('/posts/search', { params });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro na busca de postagens' 
      };
    }
  },

  // Postagens recentes
  getRecent: async (limit = 5) => {
    try {
      const response = await api.get('/posts/recent', { params: { limit } });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar postagens recentes' 
      };
    }
  },

  // Minhas postagens
  getMyPosts: async () => {
    try {
      const response = await api.get('/my-posts');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar minhas postagens' 
      };
    }
  },

  // Criar postagem
  create: async (postData) => {
    try {
      const response = await api.post('/posts', postData);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao criar postagem' 
      };
    }
  },

  // Atualizar postagem
  update: async (id, postData) => {
    try {
      const response = await api.put(`/posts/${id}`, postData);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao atualizar postagem' 
      };
    }
  },

  // Deletar postagem
  delete: async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao deletar postagem' 
      };
    }
  },

  // Publicar postagem
  publish: async (id) => {
    try {
      const response = await api.post(`/posts/${id}/publish`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao publicar postagem' 
      };
    }
  },

  // Despublicar postagem
  unpublish: async (id) => {
    try {
      const response = await api.post(`/posts/${id}/unpublish`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao despublicar postagem' 
      };
    }
  }
};

// Serviços para dashboard
export const dashboardService = {
  // Estatísticas de membros
  getMemberStats: async () => {
    try {
      const response = await api.get('/dashboard/stats');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar estatísticas' 
      };
    }
  },

  // Estatísticas de postagens
  getPostStats: async () => {
    try {
      const response = await api.get('/dashboard/posts/stats');
      return { success: true, data: response.data.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Erro ao buscar estatísticas de postagens' 
      };
    }
  }
};

export default api;
